import React, { useState, useEffect, useContext } from "react";
import { CircularProgress, Tabs, Tab } from "@mui/material";
import { PackageIcon, PlusIcon, PencilIcon, TrashIcon } from "@phosphor-icons/react";
import { AuthContext } from "../../contexts";
import { CustomPageHeader, CustomList, CustomTag, CustomModal, CustomSelect } from "../../ui";
import axios from "axios";
import { toast } from "react-toastify";
import { toastOptions } from "../../assets/constants";

const CatProduits = () => {
    const { token } = useContext(AuthContext);
    const [isLoaded, setIsLoaded] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [refreshCounter, setRefreshCounter] = useState(0);
    
    // États pour les onglets
    const [selectedTab, setSelectedTab] = useState(0);
    
    // États pour les modales des catégories
    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        description: ""
    });
    
    // États pour le mapping
    const [invoiceLabels, setInvoiceLabels] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [labelsWithMappings, setLabelsWithMappings] = useState([]);

    const columns = React.useMemo(
        () => [
            {
                Header: "Libellé",
                accessor: "name",
                icon: "package",
                Cell: ({ cell: { value } }) => <span>{value}</span>,
            },
            {
                Header: "Description",
                accessor: "description",
                icon: "note",
                Cell: ({ cell: { value } }) => <span>{value || "-"}</span>,
            },
            {
                Header: "Mappings",
                accessor: "mappings",
                icon: "link",
                Cell: ({ cell: { value } }) => (
                    <CustomTag value={value ? value.length : 0} className="blue" />
                ),
            },
            {
                Header: "Actions",
                accessor: "id",
                icon: "gear",
                Cell: ({ row }) => (
                    <div className="action-buttons-container">
                        <button className="action-button modifier-button" onClick={() => handleEdit(row.original)} title="Modifier">
                            <PencilIcon size={16} weight="bold" />
                        </button>
                        <button className="action-button delete-button" onClick={() => handleDelete(row.original)} title="Supprimer">
                            <TrashIcon size={16} weight="bold" />
                        </button>
                    </div>
                ),
            },
        ],
        []
    );

    // Handler pour les changements de catégorie
    const handleCategoryChange = async (label, categoryId) => {
        try {
            // Vérifier si le label est vide ou null
            if (!label || label.trim() === "") {
                toast.error("Impossible de mapper un libellé vide", toastOptions);
                return;
            }

            // Chercher TOUS les mappings existants pour ce label (il ne devrait y en avoir qu'un seul normalement)
            const existingMappings = mappings.filter(mapping => mapping.label === label);
            
            if (categoryId === "") {
                // Supprimer tous les mappings existants pour ce label
                for (const mapping of existingMappings) {
                    await axios.delete(`${process.env.REACT_APP_UPA_API_HOST}pennylane/category-mappings/${mapping.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                }
                if (existingMappings.length > 0) {
                    toast.success("Mapping supprimé avec succès", toastOptions);
                }
            } else {
                if (existingMappings.length > 0) {
                    // Supprimer tous les mappings existants d'abord
                    for (const mapping of existingMappings) {
                        await axios.delete(`${process.env.REACT_APP_UPA_API_HOST}pennylane/category-mappings/${mapping.id}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                    }
                }
                
                // Créer le nouveau mapping
                await axios.post(`${process.env.REACT_APP_UPA_API_HOST}pennylane/category-mappings`, 
                    { categoryId, label: label.trim() }, 
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                if (existingMappings.length > 0) {
                    toast.success("Mapping mis à jour avec succès", toastOptions);
                } else {
                    toast.success("Mapping créé avec succès", toastOptions);
                }
            }
            
            // Recharger les données
            setRefreshCounter(prev => prev + 1);
        } catch (error) {
            console.error("Erreur lors de la modification du mapping:", error);
            toast.error(error.response?.data?.error || "Erreur lors de la modification du mapping", toastOptions);
        }
    };

    const categoryOptions = React.useMemo(() => 
        categories.map(category => ({ 
            value: category.id, 
            label: category.name 
        })), 
        [categories]
    );

    const handleCategorySelectChange = React.useCallback((label, selectedOption) => {
        const categoryId = selectedOption ? selectedOption.value : "";
        handleCategoryChange(label, categoryId);
    }, [handleCategoryChange]);

    const mappingColumns = React.useMemo(
        () => [
            {
                Header: "Label de facture",
                accessor: "label",
                icon: "note",
                Cell: ({ cell: { value } }) => (
                    <span style={!value || value.trim() === '' ? { color: '#999', fontStyle: 'italic' } : {}}>
                        {!value || value.trim() === '' ? 'Libellé vide' : value}
                    </span>
                ),
            },
            {
                Header: "Montant total",
                accessor: "totalAmount",
                icon: "currency-eur",
                Cell: ({ cell: { value } }) => (
                    <CustomTag 
                        value={`${new Intl.NumberFormat('fr-FR', { 
                            style: 'currency', 
                            currency: 'EUR' 
                        }).format(value || 0)}`} 
                        className="success" 
                    />
                ),
            },
            {
                Header: "Provenance",
                accessor: "sources",
                icon: "info",
                Cell: ({ cell: { value } }) => (
                    <CustomTag 
                        value={value || "Inconnue"} 
                        className={value && value.includes('+') ? "warning" : "primary"} 
                    />
                ),
            },
            {
                Header: "Catégorie associée",
                accessor: "categoryId",
                icon: "package",
                Cell: ({ row }) => {
                    const currentCategory = row.original.categoryId ? 
                        categoryOptions.find(opt => opt.value === row.original.categoryId) : 
                        null;
                    
                    const isEmptyLabel = !row.original.label || row.original.label.trim() === "";
                    
                    if (isEmptyLabel) {
                        return (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>
                                Impossible de mapper
                            </span>
                        );
                    }
                    
                    return (
                        <CustomSelect
                            options={categoryOptions}
                            value={currentCategory}
                            onChange={(selected) => handleCategorySelectChange(row.original.label, selected)}
                            placeholder="Aucune catégorie"
                            isSearchable
                            isClearable
                            width={200}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    );
                },
            },
        ],
        [categories, categoryOptions, handleCategorySelectChange]
    );

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/categories`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCategories(response.data);
        } catch (error) {
            console.error("Erreur lors du chargement des catégories:", error);
            toast.error("Erreur lors du chargement des catégories", toastOptions);
        } finally {
            setIsLoaded(true);
        }
    };

    const fetchInvoiceLabels = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/invoice-labels`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInvoiceLabels(response.data);
        } catch (error) {
            console.error("Erreur lors du chargement des labels:", error);
            toast.error("Erreur lors du chargement des labels de facture", toastOptions);
        }
    };

    const fetchMappings = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/category-mappings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMappings(response.data);
        } catch (error) {
            console.error("Erreur lors du chargement des mappings:", error);
            toast.error("Erreur lors du chargement des mappings", toastOptions);
        }
    };

    // Fonction pour combiner les labels avec leurs mappings existants
    const combineLabelsWithMappings = () => {
        const combined = invoiceLabels.map(labelItem => {
            const existingMapping = mappings.find(mapping => mapping.label === labelItem.label);
            return {
                label: labelItem.label,
                totalAmount: labelItem.totalAmount || 0,
                sources: labelItem.sources || "Inconnue",
                categoryId: existingMapping ? existingMapping.categoryId : null,
                mappingId: existingMapping ? existingMapping.id : null
            };
        });
        setLabelsWithMappings(combined);
    };

    useEffect(() => {
        fetchCategories();
        if (selectedTab === 1) {
            fetchInvoiceLabels();
            fetchMappings();
        }
    }, [refreshCounter, selectedTab]);

    useEffect(() => {
        if (invoiceLabels.length > 0 && selectedTab === 1) {
            combineLabelsWithMappings();
        }
    }, [invoiceLabels, mappings, selectedTab]);

    const handleAdd = () => {
        setFormData({ name: "", description: "" });
        setOpenAdd(true);
    };

    const handleEdit = (category) => {
        setCurrentCategory(category);
        setFormData({
            name: category.name || "",
            description: category.description || ""
        });
        setOpenEdit(true);
    };

    const handleDelete = (category) => {
        setCurrentCategory(category);
        setOpenDelete(true);
    };

    const handleSubmitAdd = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_UPA_API_HOST}pennylane/categories`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Catégorie créée avec succès", toastOptions);
            setOpenAdd(false);
            setRefreshCounter(prev => prev + 1);
        } catch (error) {
            console.error("Erreur lors de la création:", error);
            toast.error(error.response?.data?.error || "Erreur lors de la création", toastOptions);
        }
    };

    const handleSubmitEdit = async () => {
        try {
            await axios.put(`${process.env.REACT_APP_UPA_API_HOST}pennylane/categories/${currentCategory.id}`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Catégorie modifiée avec succès", toastOptions);
            setOpenEdit(false);
            setRefreshCounter(prev => prev + 1);
        } catch (error) {
            console.error("Erreur lors de la modification:", error);
            toast.error(error.response?.data?.error || "Erreur lors de la modification", toastOptions);
        }
    };

    const handleSubmitDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_UPA_API_HOST}pennylane/categories/${currentCategory.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Catégorie supprimée avec succès", toastOptions);
            setOpenDelete(false);
            setRefreshCounter(prev => prev + 1);
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            toast.error(error.response?.data?.error || "Erreur lors de la suppression", toastOptions);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };



    return isLoaded ? (
        <div className="c-app__view">
            <CustomPageHeader
                title="Catégories produits"
                titleIcon={{
                    icon: PackageIcon,
                }}
            />
            
            <div className="tabs-container">
                <Tabs 
                    value={selectedTab} 
                    onChange={(_, newValue) => setSelectedTab(newValue)} 
                    variant="fullWidth"
                    className="custom-tabs"
                >
                    <Tab label="Gestion des catégories" />
                    <Tab label="Mapping" />
                </Tabs>
                
                <div className="tab-content">
                    {selectedTab === 0 && (
                        <>
                            <div className="mapping-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px' }}>
                                <CustomTag className="blue" value="Gérer les catégories de produits" />
                                <button
                                    className="button-large blue-button"
                                    onClick={handleAdd}
                                >
                                    <PlusIcon size={16} />
                                    <span>Ajouter une catégorie</span>
                                </button>
                            </div>
                            <CustomList
                                entity={categories}
                                columns={columns}
                                selectedIds={selectedIds}
                                setSelectedIds={setSelectedIds}
                                showSearch={true}
                                searchableKeys={["name", "description"]}
                                isSelectable={false}
                                isEditable={false}
                                isFetchable={false}
                                isClickable={false}
                            />
                        </>
                    )}
                    
                    {selectedTab === 1 && (
                        <>
                            <div className="mapping-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px' }}>
                                <CustomTag className="blue" value="Associer des labels de facture aux catégories" />
                            </div>
                            <CustomList
                                entity={labelsWithMappings}
                                columns={mappingColumns}
                                showSearch={true}
                                searchableKeys={["label"]}
                                isSelectable={false}
                                isEditable={false}
                                isFetchable={false}
                                isClickable={false}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Modale d'ajout */}
            <CustomModal show={openAdd} setShow={setOpenAdd} title="Ajouter une catégorie">
                <div className="custom__modal-group">
                    <label className="name">Libellé</label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Nom de la catégorie..."
                        value={formData.name}
                        onChange={handleInputChange}
                        className="classic-input"
                        required
                    />
                </div>
                <div className="custom__modal-group">
                    <label className="comments">Description (optionnel)</label>
                    <textarea
                        name="description"
                        placeholder="Description de la catégorie..."
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="classic-input classic-input--textarea"
                    />
                </div>
                <div className="modal-buttons">
                    <button onClick={() => setOpenAdd(false)} className="button-large cancel-button">
                        Annuler
                    </button>
                    <button onClick={handleSubmitAdd} className="button-large green-button" disabled={!formData.name.trim()}>
                        <PlusIcon size={16} />
                        <span>Créer</span>
                    </button>
                </div>
            </CustomModal>

            {/* Modale de modification */}
            <CustomModal show={openEdit} setShow={setOpenEdit} title="Modifier la catégorie">
                <div className="custom__modal-group">
                    <label className="name">Libellé</label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Nom de la catégorie..."
                        value={formData.name}
                        onChange={handleInputChange}
                        className="classic-input"
                        required
                    />
                </div>
                <div className="custom__modal-group">
                    <label className="comments">Description (optionnel)</label>
                    <textarea
                        name="description"
                        placeholder="Description de la catégorie..."
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="classic-input classic-input--textarea"
                    />
                </div>
                <div className="modal-buttons">
                    <button onClick={() => setOpenEdit(false)} className="button-large cancel-button">
                        Annuler
                    </button>
                    <button onClick={handleSubmitEdit} className="button-large green-button" disabled={!formData.name.trim()}>
                        <PencilIcon size={16} />
                        <span>Modifier</span>
                    </button>
                </div>
            </CustomModal>

            {/* Modale de suppression */}
            <CustomModal show={openDelete} setShow={setOpenDelete} title="Confirmer la suppression">
                <div className="c-modal-content is-center">
                    <p>
                        Êtes-vous sûr de vouloir supprimer la catégorie <strong>"{currentCategory?.name}"</strong> ?
                    </p>
                    {currentCategory?.mappings?.length > 0 && (
                        <p style={{color: 'orange', marginTop: '10px'}}>
                            ⚠️ Cette catégorie contient {currentCategory.mappings.length} mapping(s) qui seront également supprimé(s).
                        </p>
                    )}
                    <p>Cette action est irréversible.</p>
                </div>
                <div className="modal-buttons">
                    <button onClick={() => setOpenDelete(false)} className="button-large cancel-button">
                        Annuler
                    </button>
                    <button onClick={handleSubmitDelete} className="button-large red-button">
                        <TrashIcon size={16} />
                        <span>Supprimer</span>
                    </button>
                </div>
            </CustomModal>

        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default CatProduits;